import { MapPin, Calendar, Clock, Check, Lock, Shield } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { useNav } from '@/shared/hooks/use-nav'
import { Card } from '@/shared/ui/card'
import { Chip } from '@/shared/ui/chip'
import { Button } from '@/shared/ui/button'
import { SeatStrip } from '@/shared/components/seat-strip'
import { MatchTypeBadge } from '@/shared/ui/match-type-badge'
import { BrandMark } from '@/shared/components/brand-mark'
import { formatNivelRange } from '@/shared/levels'
import { formatReliability } from '@/shared/format'
import { findMatch, freeSlots } from '@/shared/mock/data'
import { useAppReady } from '@/shared/hooks/use-app-ready'
import { useEffect, useRef, useState } from 'react'
import { useFloatingOffset } from '@/shared/hooks/use-floating-offset'
import { Loader2, UserPlus } from 'lucide-react'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { MembershipError } from './api/matches-repository'
import { useEnsureSession, usePreviewMatch, useGuestJoin } from './use-preview-match'
import { useClaimPlaceholder } from './use-matches'
import { authRepository } from '@/features/auth/api/auth-repository'
import { usePublicProfile } from '@/features/jugadores/use-public-profile'
import { showToast } from '@/shared/ui/toast'
import {
  doorState,
  trackPreviewViewed,
  trackGuestJoinStarted,
  trackGuestJoinFailed,
  trackGuestJoinCompleted,
  trackGuestSignupStarted,
  trackPlaceholderClaimed,
} from '@/core/funnel'

// PUBLIC shared-link preview (docs/match-sharing.md): what a WhatsApp recipient
// WITHOUT the app sees at /match/{id} — match summary + join-by-name. No auth,
// no tab bar.
//
// A PRIVATE match is joinable here too (2026-08-08). It used to show the
// preview and no join path, which locked out exactly the people this door
// exists for: the friends an organizer reaches by WhatsApp, who have no account
// and therefore no invite doc. "Privado" means unlisted, not invite-only —
// see docs/product/spec/usersmatch/02-matches.md.
export function MatchPreviewPage() {
  const { id } = useParams()
  const navigate = useNav()
  const session = useEnsureSession()
  const real = usePreviewMatch(id, session.uid)
  const organizerPub = usePublicProfile(AUTH_BYPASS ? null : real.data?.organizerUid)
  const guestJoin = useGuestJoin()
  const claimSeat = useClaimPlaceholder()
  // Returning guests keep their name — a shared-link regular types it once.
  const [guestName, setGuestName] = useState(() => localStorage.getItem('um-guest-name') ?? '')
  const [joinedAs, setJoinedAs] = useState<string | null>(null)
  const [attempted, setAttempted] = useState(false)
  const match = AUTH_BYPASS ? findMatch(id) : real.data
  // This page renders its own loading/error state, including before guest auth.
  useAppReady()

  // Top of the funnel: someone opened a shared link. Once per match id, and
  // BEFORE the member redirect below — a full account opening a link is a real
  // open and worth counting, but as `member`, because averaging it in with
  // strangers is how a share loop looks healthier than it is. Under
  // AUTH_BYPASS `session.uid` stays null forever, so the stub walk never
  // reaches this.
  // The join block is this screen's bottom chrome — publish its height so the
  // update banner and toasts cannot land on "Unirme al partido".
  const setBottomChrome = useFloatingOffset()

  const viewedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!id || session.uid === null || real.isPending) return
    if (viewedRef.current === id) return
    viewedRef.current = id
    const m = real.data
    trackPreviewViewed(
      id,
      real.isError ? 'error' : doorState(m, m ? freeSlots(m) : 0),
      session.isAnonymous ? 'guest' : 'member',
      m?.privacy,
    )
  }, [id, session.uid, session.isAnonymous, real.isPending, real.isError, real.data])

  // A full account already signed in? The in-app detail is strictly better.
  // Effect, not inline: navigating during render trips React's
  // setState-in-render error through RouterProvider.
  const forwardToDetail = !AUTH_BYPASS && !!session.uid && !session.isAnonymous && !!match
  useEffect(() => {
    if (forwardToDetail) void navigate(`/partido/${id}`, { replace: true })
  }, [forwardToDetail, id, navigate])

  const restricted =
    match?.genderCategory === 'men' ||
    match?.genderCategory === 'women' ||
    match?.genderCategory === 'mixed'
  // Seats somebody typed a name into that nobody has taken yet.
  const unclaimedSeats = (match?.players ?? [])
    .filter((p) => p.identity === 'placeholder' && p.uid)
    .map((p) => ({ uid: p.uid as string, name: p.name }))

  async function onClaim(placeholderUid: string, name: string) {
    if (!match || claimSeat.isPending) return
    navigator.vibrate?.(12)
    trackGuestJoinStarted(match.id)
    try {
      const user = await authRepository.ensureSignedIn()
      await claimSeat.mutateAsync({
        matchId: match.id,
        placeholderUid,
        claimer: {
          uid: user.uid,
          name: guestName.trim() || name,
          photoURL: null,
          gender: null,
          isGuest: user.isAnonymous,
        },
      })
      trackPlaceholderClaimed(match.id, { beforeKickoff: true })
      trackGuestJoinCompleted(match.id)
      localStorage.setItem('um-guest-name', guestName.trim() || name)
      setJoinedAs(guestName.trim() || name)
    } catch (e) {
      navigator.vibrate?.(20)
      const reason = e instanceof MembershipError ? e.reason : ''
      trackGuestJoinFailed(match.id, reason || 'claim-failed')
      showToast(
        reason === 'already-joined'
          ? 'Ya estás en este partido.'
          : 'Esa plaza ya no está disponible.',
      )
    }
  }

  async function onGuestJoin() {
    if (!match || guestJoin.isPending) return
    trackGuestJoinStarted(match.id)
    if (!guestName.trim()) {
      // Counted as a failure, not skipped: tapping the button with an empty
      // field is the cheapest drop-off there is, and the one most likely to be
      // a layout problem rather than a change of mind.
      trackGuestJoinFailed(match.id, 'no_name')
      setAttempted(true)
      navigator.vibrate?.(20)
      return
    }
    navigator.vibrate?.(12)
    if (AUTH_BYPASS) {
      // Design-stub parity: the fixture ids don't exist in Firestore, so the
      // walk sees the same success state the real transaction produces.
      localStorage.setItem('um-guest-name', guestName.trim())
      setJoinedAs(guestName.trim())
      return
    }
    try {
      await guestJoin.mutateAsync({ matchId: match.id, name: guestName.trim() })
      trackGuestJoinCompleted(match.id)
      localStorage.setItem('um-guest-name', guestName.trim())
      setJoinedAs(guestName.trim())
    } catch (e) {
      navigator.vibrate?.(20)
      const reason = e instanceof MembershipError ? e.reason : ''
      trackGuestJoinFailed(match.id, reason || 'unknown')
      showToast(
        reason === 'full'
          ? 'El partido se acaba de completar.'
          : reason === 'already-joined'
            ? 'Ya estás en este partido.'
            : 'No pudimos apuntarte. Inténtalo de nuevo.',
      )
    }
  }

  if (!AUTH_BYPASS && (session.status !== 'ready' || real.isPending || real.isError)) {
    const failed = session.status === 'error' || (session.status === 'ready' && real.isError)
    return (
      <main className="min-h-full flex flex-col px-6 py-8 safe-top safe-bottom">
        <div className="flex-1 w-full max-w-sm mx-auto flex flex-col justify-center gap-5 text-center">
          <span className="text-[color:var(--brand-mint)] mx-auto" aria-hidden="true"><BrandMark size={48} /></span>
          <div role={failed ? 'alert' : 'status'}>
            <h1 className="text-[length:var(--fs-xl)] font-bold">
              {failed ? 'No pudimos abrir el partido' : 'Abriendo tu invitación'}
            </h1>
            <p className="mt-2 text-[color:var(--fg-muted)]">
              {failed
                ? 'No hemos podido conectar. Comprueba tu conexión e inténtalo de nuevo.'
                : 'Estamos conectando para mostrarte el partido.'}
            </p>
          </div>
          <Button
            size="lg"
            disabled={!failed}
            aria-busy={!failed}
            onClick={() => {
              if (session.status === 'error') session.retry()
              else void real.refetch()
            }}
          >
            {failed ? 'Reintentar' : <><Loader2 size={18} className="motion-safe:animate-spin" aria-hidden="true" /> Cargando partido…</>}
          </Button>
          <Button variant="outline" onClick={() => navigate('/login')}>
            Ya tengo cuenta · Entrar
          </Button>
        </div>
      </main>
    )
  }

  if (joinedAs && match) {
    return (
      <main className="min-h-full flex flex-col px-6 py-8 safe-top safe-bottom">
        <div className="flex-1 w-full max-w-sm mx-auto flex flex-col justify-center gap-5 um-fade text-center">
          <span className="text-[color:var(--brand-mint)] mx-auto"><BrandMark size={48} /></span>
          <div>
            <h1 className="text-[length:var(--fs-xl)] font-bold">¡Dentro, {joinedAs.split(' ')[0]}!</h1>
            <p className="text-[color:var(--fg-muted)] mt-1">
              Tienes plaza en {match.place} · {match.dayLabel} {match.timeLabel}.
            </p>
          </div>
          <Card>
            <div className="p-4 flex flex-col gap-2 text-left">
              <p className="font-semibold">Crea tu perfil en 30 segundos</p>
              <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
                Guarda tus partidos, tu nivel y entérate si algo cambia. Tu plaza ya es tuya igualmente.
              </p>
              {/* `fromMatchId` rides along so `onboarding_completed` can say it
                  came from a shared link — the last step of the funnel, and the
                  only one that turns a seat into a player. */}
              <Button
                size="lg"
                onClick={() => {
                  trackGuestSignupStarted(match.id)
                  void navigate('/onboarding', {
                    state: { guestName: joinedAs, fromMatchId: match.id },
                  })
                }}
              >
                <UserPlus size={18} /> Crear mi perfil
              </Button>
              <Button variant="outline" onClick={() => navigate(`/partido/${match.id}`)}>
                Ver el partido
              </Button>
            </div>
          </Card>
        </div>
    </main>
    )
  }

  return (
    <main className="min-h-full flex flex-col px-6 py-8 safe-top safe-bottom">
      <div className="flex-1 w-full max-w-sm mx-auto flex flex-col gap-5 um-fade">
        <div className="flex flex-col items-center text-center gap-2">
          <span className="text-[color:var(--brand-mint)]"><BrandMark size={48} /></span>
          <h1 className="text-[length:var(--fs-xl)] font-bold">
            {match ? 'Te invitan a un partido' : 'uPadelMatch'}
          </h1>
        </div>

        {!match ? (
          <p className="text-center text-[color:var(--fg-muted)]">
            Este partido ya no está disponible — puede que se haya jugado o cancelado.
          </p>
        ) : (
          <>
            <Card>
              <div className="p-4 flex flex-col gap-3">
                <div>
                  <p className="font-bold text-[length:var(--fs-lg)]">{match.place}</p>
                  <p className="flex items-center gap-1 text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
                    <MapPin size={13} /> {match.city}
                  </p>
                </div>
                <div className="flex items-center gap-4 text-[length:var(--fs-sm)]">
                  <span className="flex items-center gap-1.5 font-semibold">
                    <Calendar size={14} className="text-[color:var(--fg-faint)]" /> {match.dayLabel}
                  </span>
                  <span className="flex items-center gap-1.5 font-semibold">
                    <Clock size={14} className="text-[color:var(--fg-faint)]" /> {match.timeLabel}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Chip>{formatNivelRange(match.levelMin, match.levelMax)}</Chip>
                  {/* `freeSlots > 0` — without it a FULL match advertised
                      "Busca Revés" directly beside its own "Completo" a few
                      lines below. Same defect fixed on MatchCard the same day;
                      an audit reported this file as already guarded and it was
                      not. Checked, not assumed. */}
                  {freeSlots(match) > 0 && match.position !== 'Indiferente' && (
                    <Chip>Busca {match.position}</Chip>
                  )}
                  <MatchTypeBadge type={match.matchType} />
                  {/* The price was on the member detail and in the WhatsApp
                      message (💰 in `matchShareText`) but NOT here — the one
                      screen where a stranger actually commits. They read a cost
                      in a chat bubble, tapped, typed their name, and joined a
                      match whose page never mentioned money. Same formatting as
                      the detail chip, so the two views of one match agree. */}
                  {match.cost != null && (
                    <Chip>{match.cost.toFixed(2).replace('.', ',')} €/jugador</Chip>
                  )}
                  {match.courtReserved && (
                    <Chip className="text-[color:var(--ok)] border-[var(--ok)]">
                      <Check size={13} /> Con pista
                    </Chip>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  {/* One box per seat — a stranger deciding whether to join
                      should be able to COUNT the room, not read a total and
                      subtract. The old stack drew players only, so companions
                      were invisible here while occupying real seats. */}
                  <SeatStrip
                    players={match.players}
                    companions={match.offAppCompanions ?? 0}
                    capacity={match.capacity}
                  />
                  <span className="text-[length:var(--fs-sm)] font-semibold text-[color:var(--accent)]">
                    {freeSlots(match) === 1
                      ? '1 plaza libre'
                      : freeSlots(match) > 1
                        ? `${freeSlots(match)} plazas libres`
                        : 'Completo'}
                  </span>
                </div>
                <p className="flex items-center gap-2 text-[length:var(--fs-xs)] text-[color:var(--fg-muted)]">
                  <Shield size={13} className="text-[color:var(--ok)]" />
                  Organiza {match.organizer} ·{' '}
                  {AUTH_BYPASS
                    ? formatReliability(match.organizerMatches, match.organizerNoShows)
                    : organizerPub.data
                      ? formatReliability(organizerPub.data.matchesPlayed, organizerPub.data.noShows)
                      : 'sin partidos registrados aún'}
                </p>
              </div>
            </Card>

            {match.privacy === 'Privado' && (
              <p className="flex items-start gap-2 text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
                <Lock size={15} className="shrink-0 mt-0.5" />
                Partido privado — no aparece en el feed. Tienes el enlace, así que puedes unirte.
              </p>
            )}
          </>
        )}
      </div>

      <div ref={setBottomChrome} className="w-full max-w-sm mx-auto flex flex-col gap-2 pt-4">
        {/* The privacy gate lived HERE too, and this is the one that mattered:
            `match.privacy !== 'Privado'` suppressed the whole join block, so a
            WhatsApp guest landing on the share door — the single screen this
            flow exists for — saw no way in at all. Fixing the rules alone would
            not have fixed the product. */}
        {match && freeSlots(match) > 0 && match.status === 'active' && (
          restricted ? (
            <p className="text-center text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
              Partido{' '}
              {match.genderCategory === 'women'
                ? 'solo chicas'
                : match.genderCategory === 'men'
                  ? 'solo chicos'
                  : 'mixto (2 chicas y 2 chicos)'}{' '}
              — necesitas una
              cuenta con tu perfil para unirte.
            </p>
          ) : (
            <>
            {/* CLAIMING beats joining when a seat already carries a name.
                Somebody typed it, the seat is already paid for, and claiming
                carries its history — whereas joining fresh would consume ANOTHER
                seat and leave the named one unclaimed. This is the step the
                whole placeholder model is a bet on (spec/02 § The bet), so it
                is offered FIRST in reading order.
                OUTLINE, not accent: it is addressed BY NAME, so the one person
                it is for will find it without shouting — while everyone else on
                this screen is a stranger for whom "Unirme al partido" is the
                real action. Two accent-filled buttons stacked is the exact
                defect removed from this screen days ago (the install button),
                and the visual review caught me reintroducing it. One accent per
                screen, and it belongs to the majority case. */}
            {unclaimedSeats.length > 0 && (
              <div className="flex flex-col gap-2 pb-1">
                {unclaimedSeats.map((seat) => (
                  <Button
                    key={seat.uid}
                    variant="outline"
                    size="lg"
                    disabled={claimSeat.isPending}
                    onClick={() => void onClaim(seat.uid, seat.name)}
                  >
                    ¿Eres tú, {seat.name.split(' ')[0]}?
                  </Button>
                ))}
                <p className="text-center text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]">
                  Alguien ya te guardó plaza. Tómala y es tuya.
                </p>
              </div>
            )}
            <div className="flex flex-col gap-2">
              {/* The objection this screen has to answer, answered where it is
                  asked. Someone looking at a blank name field does not know
                  whether typing it signs them up for something — and the
                  promise was already written, in the link-preview card's
                  og:description, on a page that then never repeated it. Same
                  words, so the card and the screen behind it agree. */}
              <p className="text-center text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
                Sin crear cuenta ni descargar nada.
              </p>
              <input
                className="field"
                placeholder="Tu nombre"
                autoComplete="name"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
              />
              {attempted && !guestName.trim() && (
                <span className="text-[length:var(--fs-xs)] text-[color:var(--bad)]">
                  Dinos tu nombre para guardarte la plaza.
                </span>
              )}
              <Button size="lg" disabled={guestJoin.isPending} aria-busy={guestJoin.isPending} onClick={() => void onGuestJoin()}>
                {guestJoin.isPending ? (
                  <><Loader2 size={18} className="motion-safe:animate-spin" /> Guardando tu plaza…</>
                ) : (
                  'Unirme al partido'
                )}
              </Button>
            </div>
            </>
          )
        )}
        {/* "Instalar la app" used to render here for PRIVATE matches only. It
            was a consolation prize: a private match offered no join button, so
            install was the only action left on the screen. Private matches are
            joinable now (2026-08-08), and the button became a second
            accent-FILLED control sitting directly under "Unirme al partido" —
            measured identical fill, identical size, stacked. Two elements
            competing to win the squint test, on the single screen this app
            acquires players through.
            A guest who lands from WhatsApp came to join a match, not to install
            an app. Install still has two homes for someone who actually wants
            it — Perfil and the push nudge — so removing it here orphans
            nothing. The sheet it opened went with it rather than being left
            mounted and unreachable. */}
        {/* A dead link is still a warm lead — someone tapped a padel invitation
            in WhatsApp. This state used to offer them ONE action, "Ya tengo
            cuenta · Entrar", which is a login for an account they do not have
            by definition. That is a dead end at the exact moment somebody is
            curious, and expired links get common once matches turn over.
            `mode: 'signup'` so they land on the form that applies to them
            rather than having to find the toggle. */}
        {!match && (
          <Button
            size="lg"
            onClick={() => navigate('/login', { state: { mode: 'signup' } })}
          >
            Crear cuenta y buscar partido
          </Button>
        )}
        <Button variant="outline" onClick={() => navigate('/login')}>
          Ya tengo cuenta · Entrar
        </Button>
      </div>
    </main>
  )
}
